'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import SellerVerificationStepper from './SellerVerificationStepper';
import { ShieldCheck, Loader2 } from 'lucide-react';

export default function SellerVerificationClient() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const [verificationData, setVerificationData] = useState<any>(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      router?.replace('/sign-up-login-screen');
    }
  }, [user, loading, router]);

  useEffect(() => {
    if (!user) return;
    const fetchVerification = async () => {
      setFetching(true);
      try {
        // Ensure verification record exists for owner
        const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();

        if (profile?.role !== 'owner') {
          router?.replace('/property-listings');
          return;
        }

        const { data } = await supabase?.from('seller_verifications')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();

        if (!data) {
          // Create verification record if it doesn't exist
          const { data: newRecord } = await supabase?.from('seller_verifications')?.insert({ user_id: user?.id, email_confirmed: false, overall_status: 'pending' })?.select()?.single();
          setVerificationData(newRecord ? {
            emailConfirmed: newRecord?.email_confirmed,
            idDocumentType: newRecord?.id_document_type,
            idDocumentUrl: newRecord?.id_document_url,
            idDocumentBackUrl: newRecord?.id_document_back_url,
            antiFraudStatus: newRecord?.anti_fraud_status,
            overallStatus: newRecord?.overall_status,
          } : null);
        } else {
          setVerificationData({
            emailConfirmed: data?.email_confirmed,
            idDocumentType: data?.id_document_type,
            idDocumentUrl: data?.id_document_url,
            idDocumentBackUrl: data?.id_document_back_url,
            antiFraudStatus: data?.anti_fraud_status,
            overallStatus: data?.overall_status,
          });
        }
      } catch {
        setVerificationData(null);
      } finally {
        setFetching(false);
      }
    };
    fetchVerification();
  }, [user]);

  if (loading || fetching) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Page Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-50 rounded-2xl mb-4">
          <ShieldCheck size={28} className="text-primary-500" />
        </div>
        <h1 className="text-2xl font-700 text-gray-900 mb-2">Seller Verification</h1>
        <p className="text-gray-500 text-sm max-w-md mx-auto">
          Complete all three steps to verify your identity and start listing properties on Directly.
        </p>
      </div>
      <SellerVerificationStepper
        initialData={verificationData}
        onVerified={() => router?.push('/property-management')}
      />
    </div>
  );
}
